/**
 * CharacterStage — the layered avatar renderer.
 * Stacks equipped accessory layers (base → front) and runs a squash-and-stretch
 * "poke" when tapped. No idle bob (matches the final design).
 */

import { useCallback, useRef } from 'react';
import { Animated, Easing, Pressable } from 'react-native';
import { LayeredAvatarView } from './LayeredAvatarView';
import { characterStageStyles as styles } from './CharacterStage.styles';
import type { EquippedMap } from './characterCustomizer.types';

interface CharacterStageProps {
  equipped: EquippedMap;
  size: number;
  onPoke?: () => void;
}

const POKE_INPUT = [0, 0.22, 0.48, 0.72, 1];

export function CharacterStage({ equipped, size, onPoke }: CharacterStageProps) {
  const poke = useRef(new Animated.Value(0)).current;

  const handlePress = useCallback(() => {
    poke.setValue(0);
    Animated.timing(poke, {
      toValue: 1,
      duration: 560,
      easing: Easing.bezier(0.2, 0.8, 0.2, 1),
      useNativeDriver: true,
    }).start();
    onPoke?.();
  }, [onPoke, poke]);

  const scaleX = poke.interpolate({ inputRange: POKE_INPUT, outputRange: [1, 1.07, 0.95, 1.02, 1] });
  const scaleY = poke.interpolate({ inputRange: POKE_INPUT, outputRange: [1, 0.9, 1.07, 0.98, 1] });

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="imagebutton"
      accessibilityLabel="Your character — tap to react"
      style={[styles.root, { width: size, height: size }]}
    >
      <Animated.View style={[styles.layerStack, { transform: [{ scaleX }, { scaleY }] }]}>
        <LayeredAvatarView equipped={equipped} size={size} rounded={false} />
      </Animated.View>
    </Pressable>
  );
}
